import { SupportedLocales, SupportedCountries } from '../assets/locale'
import { UserLocale } from '../types'

/**
 * Retrieves a valid user locale based on the provided language and country.
 *
 * This function performs a three-tier fallback logic:
 * 1. First, checks if the exact language_country combination is supported
 * 2. If not, attempts to find a supported locale for the same country with a different language
 * 3. If no match is found, returns the default en_US locale
 *
 * @param language - The language code (e.g., 'en', 'fr', 'de')
 * @param country - The country code (e.g., 'US', 'CA', 'FR')
 * @returns A UserLocale object containing the resolved language and country
 * @example
 * getUserLocale('en', 'US') // returns { language: 'en', country: 'US' }
 * getUserLocale('de', 'FR') // returns { language: 'fr', country: 'FR' } (fallback to FR's supported language)
 * getUserLocale('xx', 'XX') // returns { language: 'en', country: 'US' } (default fallback)
 */
export function getUserLocale(language: string, country: string): UserLocale {
  if (SupportedLocales.includes(`${language}_${country}`)) {
    return { language, country }
  }

  if (SupportedCountries.has(country)) {
    const locale = SupportedLocales.find((locale) =>
      country === locale.split('_')[1] ? locale : undefined
    )
    if (locale) {
      const [lang, cc] = locale.split('_')
      return { language: lang, country: cc }
    }
  }

  return { language: 'en', country: 'US' }
}

/**
 * Finds a locale based on the provided language and supported country.
 *
 * @param language - The language code to find a locale for
 * @param supportedCountry - The supported country code
 * @returns The matched user locale object with language and country
 */
const findLocale = (language: string, supportedCountry: string): UserLocale => {
  return getUserLocale(language, supportedCountry)
}

/**
 * Checks if the browser's locale is supported by comparing the selected
 * localization locale against the original browser locale and supported country.
 *
 * @param selectedLocalizationLocale - The locale object resolved from browser language
 * @param browserLanguage - The browser's original language code
 * @param supportedCountry - The supported country code
 * @returns True if the browser's locale is fully supported, false otherwise
 */
const checkIsLocaleSupported = (
  selectedLocalizationLocale: UserLocale,
  browserLanguage: string,
  supportedCountry: string
): boolean => {
  return (
    selectedLocalizationLocale.language === browserLanguage &&
    selectedLocalizationLocale.country === supportedCountry
  )
}

/**
 * Determines the default locale based on fallback preferences.
 *
 * @param shouldFallbackLocalization - If true, uses the browser's locale as fallback
 * @param browserLocale - The browser's current language and country
 * @param supportedLanguage - The supported language from configuration
 * @param supportedCountry - The supported country from configuration
 * @returns The default locale to use when browser locale is not supported
 */
const getDefaultLocale = (
  shouldFallbackLocalization: boolean,
  browserLocale: { language: string; country: string },
  supportedLanguage: string,
  supportedCountry: string
): UserLocale => {
  return shouldFallbackLocalization
    ? browserLocale
    : findLocale(supportedLanguage, supportedCountry)
}

/**
 * Determines the locale to use for dashboard-initiated flows.
 *
 * For dashboard flows, we always use the user's preferred locale from the database,
 * which is determined by the supported language and country configuration.
 * This ensures consistency between what's stored in the DB and what the user sees,
 * regardless of their browser locale.
 *
 * @param supportedLanguage - The supported language from configuration (user's preferred language)
 * @param supportedCountry - The supported country from configuration (user's preferred country)
 * @returns The user's preferred locale from database configuration
 */
const getDashboardLocale = (
  supportedLanguage: string,
  supportedCountry: string
): UserLocale => {
  return findLocale(supportedLanguage, supportedCountry)
}

/**
 * Selects the appropriate locale based on whether the browser locale is supported.
 *
 * @param isLocaleSupported - Whether the browser's locale is supported
 * @param selectedLocalizationLocale - The locale resolved from browser language
 * @param defaultLocale - The default locale to use as fallback
 * @returns The selected locale (browser locale if supported, otherwise default)
 */
const selectLocale = (
  isLocaleSupported: boolean,
  selectedLocalizationLocale: UserLocale,
  defaultLocale: UserLocale
): UserLocale => {
  return isLocaleSupported ? selectedLocalizationLocale : defaultLocale
}

/**
 * Validates and determines the appropriate locale to use based on user preferences,
 * browser settings, and flow context.
 *
 * Logic:
 * - For dashboard-initiated flows: Always use the user's preferred locale from DB
 *   (passed as defaultLocale). This ensures the locale matches what's stored,
 *   even if the browser is set to a different locale.
 * - For other flows: Use the browser's locale if it's supported, otherwise fall back
 *   to the default locale.
 *
 * @param localization - The browser's current language and country
 * @param supportedLocale - The supported language and country configuration
 * @param shouldFallbackLocalization - If true, uses browser locale as fallback
 * @param fromDashboard - True when the flow is initiated from the dashboard
 * @returns The validated locale to use
 */
export const validateSelectedLocale = (
  localization: { language: string; country: string },
  supportedLocale: { supportedCountry: string; supportedLanguage: string },
  shouldFallbackLocalization?: boolean,
  fromDashboard?: boolean
): UserLocale => {
  if (fromDashboard) {
    return getDashboardLocale(
      supportedLocale.supportedLanguage,
      supportedLocale.supportedCountry
    )
  }

  const selectedLocalizationLocale = findLocale(
    localization.language,
    supportedLocale.supportedCountry
  )

  const isLocaleSupported = checkIsLocaleSupported(
    selectedLocalizationLocale,
    localization.language,
    supportedLocale.supportedCountry
  )

  const defaultLocale = getDefaultLocale(
    shouldFallbackLocalization,
    localization,
    supportedLocale.supportedLanguage,
    supportedLocale.supportedCountry
  )

  return selectLocale(
    isLocaleSupported,
    selectedLocalizationLocale,
    defaultLocale
  )
}

const NORTH_AMERICA_LOCALES = ['en_US', 'en_CA', 'en_PR', 'fr_CA']

export const isNorthAmerica = (locale: string) => {
  return NORTH_AMERICA_LOCALES.includes(locale)
}
